import './index.css'

import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Outlet } from 'react-router-dom'

import { AUDIO_FILE } from '@disco/constants/FileTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  bulkDeselectAll,
  bulkDeselectTrack,
  bulkGetTracksForPlaylist,
  bulkSelectAll,
  bulkSelectTrack,
} from '@disco/state-management/actions/BulkActions'
import {
  addWriterSplit,
  bulkUpdateTrackMetadata,
  initEditedTrack,
  updateEditedTrack,
} from '@disco/state-management/actions/ServerActions'
import {
  clearBulkEdit,
  clearBulkSaveResults,
} from '@disco/state-management/reducers/rightsManagement'
import { getTerritories } from '@disco/state-management/selectors/groups'
import {
  getConstanceSettings,
  getCustomFields,
  getHideTrackMetadataMoreButton,
  getHideWritersTab,
  getTerritoriesEnabled,
  isNativeTagsEnabled,
} from '@disco/state-management/selectors/user'
import {
  getCommonBasicMetadataFields,
  getCommonExtraMetadataFields,
} from '@disco/utils/commonMetadata'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { getCommonGroups } from '@disco/utils/groups'

import type { Playlist } from 'types/playlist'
import type { Track, TrackId } from 'types/track'

import { LuX } from '@react-icons/all-files/lu/LuX'
import CopyMetadataButton from 'components/common/tracks/copy-metadata-button'
import NativeTagsTab from 'components/common/tracks/native-tags-tab'
import TrackTerritories from 'components/common/tracks/track-territories'
import { Box, Flex, IconButton, Separator, Skeleton, Text } from 'components/ds'
import DocumentHeadTags from 'components/document-head-tags'
import {
  MetadataModalShell,
  metadataModalContentStyle,
} from 'components/modals/track/metadata-modal-shell'
import MetadataTabs from 'components/modals/track/metadata-tabs'
import DiscoAsyncModal from 'components/ui/disco-async-modal'
import defaultModalStyles from 'components/utils/modalStyles'

import type { RootState } from 'store/configureStore'

type OwnProps = {
  editing:
    | {
        tracks: Array<Track>
      }
    | {
        playlist: Playlist & {
          tracks: TrackId[]
        }
      }
  onRequestClose: () => void
}

// we allow adding writer splits only to audio files
const getWriterTrackIds = items =>
  items
    .filter(item => getFileTypeByExt(item.ext) === AUDIO_FILE)
    .map(item => item.id)

const selectedFilesHaveAudio = items => {
  const selectedFiles = items.filter(item => item.isSelected)
  return selectedFiles.some(f => getFileTypeByExt(f.ext) === AUDIO_FILE)
}

function mapStateToProps(state: RootState) {
  const { tracks, isLoading } = state.bulk
  const { editedTrack, originalEditedTrackMetadata } = state.server
  const selectedTracks = tracks.filter(t => t.isSelected)
  const resolvedTracks = selectedTracks.flatMap(t => {
    const track = state.tracks[t.trackId]
    return track ? [track] : []
  })
  return {
    saving: state.server.saving,
    error: state.bulk.error,
    showError: state.server.showError,
    editedTrack,
    isLoading,
    originalEditedTrackMetadata,
    selectedTrackIds: resolvedTracks.map(t => t.id),
    selectedTracks: resolvedTracks,
    hideMoreButton: getHideTrackMetadataMoreButton(state),
    hideWritersTab: getHideWritersTab(state),
    hideTerritoriesTab: !getTerritoriesEnabled(state),
    tracks: tracks.map(t => ({
      ...state.tracks[t.trackId],
      isSelected: t.isSelected,
    })),
    customFields: getCustomFields(state),
    territories: getTerritories(state),
    nativeTagsEnabled: isNativeTagsEnabled(state),
    bulkEditAllVersionsTabEnabled:
      !!getConstanceSettings(state)[
        'SUB-1052-BULK-EDIT-ALL-VERSIONS-TAB-ROLLOUT'
      ],
  }
}

const actions = {
  bulkGetTracksForPlaylist,
  initEditedTrack,
  bulkUpdateTrackMetadata,
  updateEditedTrack,
  bulkSelectTrack,
  bulkDeselectTrack,
  bulkSelectAll,
  bulkDeselectAll,
  addWriterSplit,
  sendMixpanelEvent,
  clearBulkEdit,
  clearBulkSaveResults,
}

const connector = connect(mapStateToProps, actions)

type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps & OwnProps

class BulkEditor extends React.Component<Props> {
  _loadData = async () => {
    if ('playlist' in this.props.editing) {
      const { playlist } = this.props.editing
      this.props.initEditedTrack({
        id: null,
        url: null,
        // Placeholder until updateEditedTrack fills it in once
        // bulkGetTracksForPlaylist resolves. DiscoAsyncModal discards this
        // subtree's output while isLoading, so nothing reads it.
        metadata: {},
        trackIds: playlist.tracks,
        comments: [],
        track_writers: [],
        playlistVersionId: playlist.id,
      })
      const result = await this.props.bulkGetTracksForPlaylist(playlist)

      // After fetching tracks, update editedTrack with the actual track IDs
      // This ensures trackIds is populated when navigating directly to the bulk-edit URL
      // where playlist.tracks might be empty initially (playlist loaded without track data)
      if (result?.tracks) {
        const trackIds = [...new Set(result.tracks.map(t => t.id))]
        const extraMeta = getCommonExtraMetadataFields(
          result.tracks.map(t => t.metadata),
        )

        this.props.updateEditedTrack({
          ...this.props.editedTrack,
          trackIds,
          metadata: {
            ...getCommonBasicMetadataFields(result.tracks),
            ...extraMeta,
          },
        })
      }

      return result
    } else if ('tracks' in this.props.editing) {
      const { tracks } = this.props.editing
      this.props.initEditedTrack({
        id: null,
        url: null,
        // all tracks are loaded, can safely display some info
        metadata: {
          ...getCommonBasicMetadataFields(tracks),
          ...getCommonExtraMetadataFields(tracks.map(t => t.metadata)),
        },
        trackIds: tracks.map(t => t.id),
        comments: [],
        track_writers: [],
        groups: getCommonGroups(tracks),
      })
    } else {
      // never happens
    }
  }
  _onClose = () => {
    if (!this.props.saving) {
      // Clear bulk editing state and error results
      this.props.clearBulkEdit()
      this.props.clearBulkSaveResults()
      this.props.onRequestClose()
    }
  }

  _renderHeading({ loading }: { loading?: boolean } = {}) {
    const num = this.props.tracks.length
    const trackIds = this.props.editedTrack.trackIds ?? []
    return (
      <>
        {/* Matches the single-track modal's header title (MediaItem large).
            The count isn't known until the tracks load, so stand in for the
            title rather than momentarily claiming "Edit 0 tracks". */}
        {loading ? (
          <Box width="140px">
            <Skeleton height="20px" />
          </Box>
        ) : (
          <Text as="div" size="3" weight="bold">
            Edit {num} <span>{num > 1 ? 'tracks' : 'track'}</span>
          </Text>
        )}
        <Flex align="center" gap="2">
          {trackIds.length > 0 && (
            <>
              <CopyMetadataButton variant="icon" trackIds={trackIds} />
              <Separator
                orientation="vertical"
                size="1"
                className="BulkEditor_HeaderDivider"
              />
            </>
          )}
          <IconButton
            size="2"
            variant="ghost"
            color="gray"
            aria-label="Close"
            onClick={this._onClose}
          >
            <LuX size={20} />
          </IconButton>
        </Flex>
      </>
    )
  }

  render() {
    const { isLoading, error } = this.props
    return (
      <div>
        <DocumentHeadTags title="Edit multiple tracks | DISCO" />
        <DiscoAsyncModal
          style={{
            ...defaultModalStyles,
            overlay: {
              ...defaultModalStyles.overlay,
              justifyContent: 'center',
            },
            content: metadataModalContentStyle(defaultModalStyles.content),
          }}
          fallbackTitle="Edit multiple tracks"
          isLoading={isLoading}
          error={error}
          onRequestClose={this._onClose}
          loadData={this._loadData}
          renderLoading={() => (
            <MetadataModalShell
              width={640}
              header={this._renderHeading({ loading: true })}
              loading
            >
              {null}
            </MetadataModalShell>
          )}
          render={() => (
            <MetadataModalShell width={640} header={this._renderHeading()}>
              {this._renderCommonFields()}
            </MetadataModalShell>
          )}
        />
        <Outlet />
      </div>
    )
  }

  _renderListOptions() {
    const { selectedTracks, tracks } = this.props
    return (
      <div className="BulkEditor_ListOptions">
        {selectedTracks.length < tracks.length && (
          <button className="BulkEditor_Option" onClick={this._handleSelectAll}>
            Select All
          </button>
        )}
        {selectedTracks.length > 1 && (
          <button
            className="BulkEditor_Option"
            onClick={this._handleSelectNone}
          >
            Deselect All
          </button>
        )}
      </div>
    )
  }

  _addWriterSplit = (split: any) => {
    const tracks = getWriterTrackIds(this.props.selectedTracks)
    if (!tracks.length) return
    const tracksSplit = { ...split, track_ids: tracks }
    return this.props.addWriterSplit(tracksSplit)
  }

  _renderCommonFields() {
    const { tracks } = this.props
    const tagsTab =
      this.props.nativeTagsEnabled && tracks.every(t => t.native_tags) ? (
        <NativeTagsTab
          tracks={tracks as any as Track[]}
          hideCopyMetadata
          onClose={this._onClose}
        />
      ) : null
    const territoriesTab =
      this.props.territories.length > 0 && !this.props.hideTerritoriesTab ? (
        <TrackTerritories
          tracks={tracks as any as Track[]}
          territories={this.props.territories}
          bulkEditingMode
          onClose={this._onClose}
        />
      ) : null
    return (
      <div onSubmit={this._handleSubmit}>
        <MetadataTabs
          hasUnsavedChanges={
            this.props.selectedTracks.length > 0 &&
            !isEqual(
              this.props.editedTrack.metadata,
              this.props.originalEditedTrackMetadata,
            )
          }
          editedTrack={this.props.editedTrack}
          originalEditedTrackMetadata={this.props.originalEditedTrackMetadata}
          showDifference
          showLyrics={false}
          saving={this.props.saving}
          showSubmitButton
          showExtraMetadata
          showComments
          onClose={this._onClose}
          showWriters={
            !this.props.hideWritersTab &&
            selectedFilesHaveAudio(this.props.tracks)
          }
          tagsTab={tagsTab}
          territoriesTab={territoriesTab}
          disableTrackTitle
          disableTrackOrder
          disableWritersEditing
          hideMoreButton={this.props.hideMoreButton}
          hideCopyMetadata
          bulkEditingMode
          customFields={this.props.customFields}
          territories={this.props.territories}
          sendMixpanelEvent={this.props.sendMixpanelEvent}
          updateEditedTrack={this.props.updateEditedTrack}
          saveTrackMetadata={this._handleSubmit}
          addWriterSplit={this._addWriterSplit}
          showApplyToAllVersions={
            this.props.bulkEditAllVersionsTabEnabled &&
            this.props.selectedTracks.length === 1 &&
            !!this.props.selectedTracks[0]?.nested_tracks?.length
          }
          nestedTracks={
            this.props.selectedTracks.length === 1
              ? this.props.selectedTracks[0]?.nested_tracks ?? []
              : []
          }
        />
      </div>
    )
  }

  _toggleInList = (track: Track, index: number) => {
    if (this.props.tracks[index]?.isSelected) {
      this.props.bulkDeselectTrack(track, index)
    } else {
      this.props.bulkSelectTrack(track, index)
    }
  }
  _handleSubmit = opts => {
    const { selectedTracks } = this.props
    const { metadata } = this.props.editedTrack

    if (selectedTracks.length > 0) {
      this.props
        .bulkUpdateTrackMetadata(undefined, selectedTracks, metadata, opts)
        .then(() => this.props.onRequestClose())
        .catch(() => {})
    }

    if (selectedTracks.length > 1) {
      this.props.sendMixpanelEvent('Bulk metadata edit')
    }
  }
  _handleClose = (evt: React.SyntheticEvent<any>) => {
    evt.preventDefault()

    this._onClose()
  }
  _handleSelectAll = (evt: React.SyntheticEvent<any>) => {
    evt.preventDefault()
    this.props.bulkSelectAll()
  }
  _handleSelectNone = (evt: React.SyntheticEvent<any>) => {
    evt.preventDefault()
    this.props.bulkDeselectAll()
  }
}

export default connector(BulkEditor)
